import * as React from "react";
import {
  NormalizedDragEvent,
  useDraggable,
  useDroppable,
} from "@progress/kendo-react-common";
import { ReorderContext } from "./StopDataGrid";
import {
  InlineEditExitProps,
  RenderProps,
} from "../../../../../Grid/InlineEdit/InlineEditRowRender";
import useOuterClick from "../../../../../Grid/InlineEdit/UseOuterClick.js";
import { handleOuterClick } from "../../../../../Grid/InlineEdit/HandleOuterClick";

type DraggableRowProps = RenderProps & InlineEditExitProps;

const DraggableRow = React.forwardRef((props: DraggableRowProps, ref: any) => {
  const { dragStart, reorder } = React.useContext(ReorderContext);

  const element = React.useRef(ref);

  const [dropped, setDropped] = React.useState(false);
  const [dragged, setDragged] = React.useState(false);
  const [direction, setDirection] = React.useState<"before" | "after" | null>(
    null
  );
  const [initial, setInitial] = React.useState({ x: 0, y: 0 });

  const handlePress = (event: NormalizedDragEvent) => {
    setInitial({
      x: event.clientX - event.offsetX,
      y: event.clientY - event.offsetY,
    });
  };

  const handleDragStart = (event: NormalizedDragEvent) => {
    if (
      !event.originalEvent.target ||
      !(event.originalEvent.target as HTMLElement).dataset.dragHandle
    ) {
      return;
    }
    setDragged(true);
    dragStart(props.originalProps.dataItem);
  };

  const handleDrag = (event: NormalizedDragEvent) => {
    if (!element.current || !dragged) {
      return;
    }
    element.current.style.transform = `translateY(${
      event.clientY - initial.y + event.scrollY
    }px)`;
  };

  const handleDragEnd = () => {
    setDragged(false);
    setDropped(false);
    setInitial({ x: 0, y: 0 });
  };

  const handleRelease = () => {
    if (!element.current) {
      return;
    }
    element.current.style.transform = null;
  };

  const handleDragEnter = () => {
    setDropped(true);
    setDirection(null);
  };

  const handleDragOver = (event: NormalizedDragEvent) => {
    if (!element.current) {
      return;
    }
    const rect = element.current.getBoundingClientRect();
    setDirection(
      rect.top + rect.height / 2 <= event.pageY ? "after" : "before"
    );
  };

  const handleDragLeave = () => {
    setDropped(false);
    setDirection(null);
  };

  const handleDrop = () => {
    reorder(props.originalProps.dataItem, direction);
    setDropped(false);
    setDirection(null);
  };

  useDraggable(
    element,
    {
      onPress: handlePress,
      onDragStart: handleDragStart,
      onDrag: handleDrag,
      onDragEnd: handleDragEnd,
      onRelease: handleRelease,
    },
    { autoScroll: dragged }
  );

  useDroppable(element, {
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  });

  let trStyle: React.CSSProperties = {
    backgroundColor: props.originalProps.isAltRow
      ? "rgba(0, 0, 0, 0.04)"
      : undefined,
    userSelect: "none",
    pointerEvents: dragged ? "none" : undefined,
    opacity: dragged ? "0.8" : undefined,
  };
  if (dropped && (direction === "before" || direction === "after")) {
    trStyle = {
      ...trStyle,
      outlineStyle: "solid",
      outlineWidth: 1,
      outlineColor: "#FFCC00",
    };
  }

  // register click handler for exit inline edit
  useOuterClick(element, (e: any, tr: HTMLTableRowElement) => {
    handleOuterClick(e, tr, props.exitEdit, props.originalProps.dataItem);
  });

  return (
    <tr
      {...props.originalProps}
      {...props.tr}
      className={props.tr.props.className}
      style={trStyle}
      ref={element}
    />
  );
});

type DraggableRowRenderProps = RenderProps & InlineEditExitProps;

export function DraggableRowRender(props: DraggableRowRenderProps) {
  const element = React.useRef<HTMLTableRowElement>();

  const tr = <DraggableRow {...props} ref={element} />;

  return tr;
}
